import { MutableRefObject, useContext, useEffect, useRef } from "react";
import { FormikHelpers, FormikProps, useFormik } from "formik";
import { useCookies } from "react-cookie";
import { useRouter } from "next/router";

import { AppContext } from "@vahak/core/dist/app-context";
import { CompanyServiceType } from "@vahak/core/dist/constants/serviceTypeId";
import { useAuth } from "@vahak/core/dist/components/auth/useAuth";
import { WEB_V2_SOURCE_SCREENS } from "@vahak/core/dist/constants/event-names";
import { useCompactAuthWebV2Tracking } from "./useCompactAuthWebV2Tracking";

import { LoginFormFieldsTypes, loginFormFieldNames } from "../../login-form-new/common/contant";
import { validateLoginFormValuesForStep } from "../../login-form-new/validation/validationSchema";
import { AnimateContainerProps } from "../../login-form-new/animate-container/AnimateContainer";
import {
    buildLoginFormInitialValues,
    createRegisteredUserLoginHandler,
    LoginEventTracker,
    processNewUserRegistration,
    setOtpFieldError,
    submitOtpStep,
    submitPhoneStep,
    useLoginAuthBindings,
    useLoginStepTransition
} from "../../login-form-new/hooks/auth";
import { LoginFormAuthResult } from "../../login-form-new/hooks/loginFormAuth.types";
import { useLatest } from "../../login-form-new/hooks/useLatest";

import { autoRegisterNewUser } from "./autoRegisterNewUser";
import { COMPACT_AUTH_STEPS, CompactAuthConfig, compactAuthStepToLoginFormStep } from "./compactAuth.types";

export interface UseCompactAuthReturn {
    formik: FormikProps<LoginFormFieldsTypes>;
    activeStep: COMPACT_AUTH_STEPS;
    animation: AnimateContainerProps["animation"];
    onClickResendOtp: ReturnType<typeof useAuth>["onClickResendOtp"];
    onClickEditNumber: () => void;
}

interface CompactAuthSubmitContext {
    activeStep: COMPACT_AUTH_STEPS;
    defaultPlaceId?: string;
    globalData?: { userPlaceInfo?: { place_id?: string } };
    defaultCompanyName?: string;
    registrationServiceType?: CompanyServiceType;
    otpSubmitInFlightRef: MutableRefObject<boolean>;
    onSubmitPhoneNum: ReturnType<typeof useAuth>["onSubmitPhoneNum"];
    onSubmitOtp: ReturnType<typeof useAuth>["onSubmitOtp"];
    onSubmitCompanyDetails: ReturnType<typeof useAuth>["onSubmitCompanyDetails"];
    completeRegisteredUserLogin: (status: LoginFormAuthResult) => Promise<void>;
    completeNewUserRegistration: (values: LoginFormFieldsTypes, result: LoginFormAuthResult) => Promise<void>;
    handleEventTracking: LoginEventTracker;
    setPhoneNum?: (phone: string | number) => void;
    setShouldPreventLayoutSwitch?: (value: boolean) => void;
    transitionToStep: (step: COMPACT_AUTH_STEPS) => void;
    phoneNum?: string | number;
}

async function handleCompactAuthStepSubmit(
    values: LoginFormFieldsTypes,
    helpers: FormikHelpers<LoginFormFieldsTypes>,
    ctx: CompactAuthSubmitContext
) {
    switch (ctx.activeStep) {
        case COMPACT_AUTH_STEPS.PHONE:
            await submitPhoneStep({
                phoneNumber: values.phoneNumber,
                whatsappUpdatesEnabled: values.whatsappUpdatesEnabled,
                setPhoneNum: ctx.setPhoneNum,
                onSubmitPhoneNum: ctx.onSubmitPhoneNum,
                handleEventTracking: ctx.handleEventTracking,
                onPhoneVerified: () => ctx.transitionToStep(COMPACT_AUTH_STEPS.OTP)
            });
            return;

        case COMPACT_AUTH_STEPS.OTP:
            await submitOtpStep({
                phoneNumber: values.phoneNumber,
                otp: values.otp,
                setShouldPreventLayoutSwitch: ctx.setShouldPreventLayoutSwitch,
                onSubmitOtp: ctx.onSubmitOtp,
                handleEventTracking: ctx.handleEventTracking,
                guardRef: ctx.otpSubmitInFlightRef,
                onInvalidOtp: () => setOtpFieldError(helpers, "Please enter the right OTP"),
                onRegisteredUser: ctx.completeRegisteredUserLogin,
                onNewUser: (status) =>
                    autoRegisterNewUser({
                        values,
                        helpers,
                        otpStatus: status,
                        globalData: ctx.globalData,
                        defaultPlaceId: ctx.defaultPlaceId,
                        defaultCompanyName: ctx.defaultCompanyName,
                        defaultServiceType: ctx.registrationServiceType,
                        phoneNum: ctx.phoneNum,
                        onSubmitCompanyDetails: ctx.onSubmitCompanyDetails,
                        handleEventTracking: ctx.handleEventTracking,
                        completeNewUserRegistration: ctx.completeNewUserRegistration
                    })
            });
            return;
    }
}

/** Auth flow shared by the compact load posting and compact bidding modals (phone -> OTP -> auto-register). */
export function useCompactAuth(config: CompactAuthConfig): UseCompactAuthReturn {
    const {
        onComplete,
        onStepChange,
        sourceScreen = WEB_V2_SOURCE_SCREENS.ONB_FORM,
        isInitiatedFrom = "Homepage V2 Compact Posting",
        defaultPlaceId,
        defaultCompanyName,
        registrationServiceType
    } = config;

    const {
        phoneNum,
        referralCode,
        globalData,
        setGlobalData,
        setPhoneNum,
        setReferralCode,
        setShouldPreventLayoutSwitch
    } = useContext(AppContext);

    const [, setCookie] = useCookies<string>([]);
    const router = useRouter();
    const otpSubmitInFlightRef = useRef(false);

    const onCompleteRef = useLatest(onComplete);

    const { activeStep, animation, transitionToStep } = useLoginStepTransition({
        initialStep: COMPACT_AUTH_STEPS.PHONE,
        onStepChange
    });

    const activeStepRef = useLatest(activeStep);

    const handleEventTracking = useCompactAuthWebV2Tracking(phoneNum);

    const finishAuth = async (status: LoginFormAuthResult) => {
        setShouldPreventLayoutSwitch?.(true);
        await onCompleteRef.current(status);
    };

    const completeRegisteredUserLogin = createRegisteredUserLoginHandler({
        handleEventTracking,
        globalData,
        setGlobalData,
        onComplete: finishAuth
    });

    const completeNewUserRegistration = async (values: LoginFormFieldsTypes, result: LoginFormAuthResult) => {
        await processNewUserRegistration({
            values,
            result,
            handleEventTracking,
            router,
            setCookie,
            setReferralCode,
            setShouldPreventLayoutSwitch,
            onSuccess: (_values, result) => finishAuth(result)
        });
    };

    const submitContextRef = useRef<CompactAuthSubmitContext>({
        activeStep,
        defaultPlaceId,
        globalData,
        defaultCompanyName,
        registrationServiceType,
        otpSubmitInFlightRef,
        onSubmitPhoneNum: async () => {},
        onSubmitOtp: async () => undefined,
        onSubmitCompanyDetails: async () => undefined,
        completeRegisteredUserLogin,
        completeNewUserRegistration,
        handleEventTracking,
        setPhoneNum,
        setShouldPreventLayoutSwitch,
        transitionToStep,
        phoneNum
    });

    const formik = useFormik({
        initialValues: buildLoginFormInitialValues({
            phoneNum,
            referralCode,
            globalData,
            defaultPlaceId,
            defaultServiceType: registrationServiceType
        }),
        validate: (values) =>
            validateLoginFormValuesForStep(values, compactAuthStepToLoginFormStep(activeStepRef.current)),
        validateOnChange: true,
        validateOnMount: true,
        validateOnBlur: true,
        enableReinitialize: true,
        onSubmit: (values, helpers) => handleCompactAuthStepSubmit(values, helpers, submitContextRef.current)
    });

    useEffect(() => {
        void formik.validateForm();
    }, [activeStep, formik.validateForm]);

    const { onSubmitPhoneNum, onClickResendOtp, onSubmitOtp, onSubmitCompanyDetails } = useLoginAuthBindings({
        formik,
        phoneNum,
        isInitiatedFrom,
        userJoiningBonusEnabled: false
    });

    submitContextRef.current = {
        activeStep,
        defaultPlaceId,
        globalData,
        defaultCompanyName,
        registrationServiceType,
        otpSubmitInFlightRef,
        onSubmitPhoneNum,
        onSubmitOtp,
        onSubmitCompanyDetails,
        completeRegisteredUserLogin,
        completeNewUserRegistration,
        handleEventTracking,
        setPhoneNum,
        setShouldPreventLayoutSwitch,
        transitionToStep,
        phoneNum
    };

    const onClickEditNumber = () => {
        formik.setFieldValue(loginFormFieldNames.otp, "");
        formik.setFieldError(loginFormFieldNames.otp, undefined);
        transitionToStep(COMPACT_AUTH_STEPS.PHONE);
    };

    return {
        formik,
        activeStep,
        animation,
        onClickResendOtp,
        onClickEditNumber
    };
}
